import MasterPricing from '@/js/pricing/MasterPricing'
import utils from '@/js/utils'

/**
 * Cut Mu Principles
 */
// 1 Mu = 120s

// Cut Mu Factors
const cutEdgeMuFactor = 1.5 / 4 // 1.5 Mu = 180s or 3mins per 4000mm so divided by 4 to get linear metre
const hingeCutMuFactor = 0.5 // 1min = pair of hinge cups
const drwCutMuFactor = 0.25 // 30s = set of drawer holes (amount is neglible)
const platsaDrwCutMuFactor = 2.9 // 350s = set of platsa drawer holes
const lightingCutMuFactor = 1.9 // 140s = set of lighting holes

/**
 * Finish Mu Principles
 */
// 1 Mu = 120s = avg of edge sand and wax per linear metre

// Finish Mu Factors
const laminateEdgeMuFactor = 2.46
const veneerEdgeMuFactor = 1
const veneerDoubleSurfaceMuFactor = 2.15
const singleMuPerimeter = 1000
const singleMuArea = 1000000 // single-sided
const doubleMuArea = 2000000 // double-sided

/**
 * Pack Mu Principles
 */
// 1 Mu = 120s

// Pack Mu Factors
const packMuFactor = 2.4 // 2.4Mu = 288s
const largePackMuFactor = 6.4 // 6.4Mu = 768s = over 0.6m2 in area
const largePackLimiter = 0.6 // 0.6m2 = (905mm x 670mm) / 1000000mm2
const plinthPackMuFactor = 3.4 // 3.4Mu = 408s = per 1000mm of plinth

export default {
  get(items, materials, handleOptions, taskType) {
    // will return object with material id as key and mu as value
    const o = {}

    if (!items) return o

    materials.forEach((material) => {
      if (!material.value || !material.thickness || !material.w || !material.h) return

      let materialMu = 0

      for (let item of items) {
        // ignore any imcomplete items
        if (item.itemNum == undefined || item.w == undefined || item.h == undefined || item.qty == undefined) continue
        // ignore if item not using this material
        if (item.material !== material.id) continue

        // set dimensions and qty - then calculate perimeter as global var
        const width = parseInt(item.w)
        const height = parseInt(item.h)
        const qty = parseInt(item.qty)

        let frontType = ''
        let coverPanelType = ''
        if (item?.frontType) {
          frontType = item.frontType.split('-')[0]
        } else if (item?.coverPanelType) {
          coverPanelType = item.coverPanelType
        }

        if (!frontType && !coverPanelType) continue // ignore if no front or cover panel type

        let cabinetType = ['lhd', 'rhd'].includes(frontType) ? item.frontType.split('-')[1] : ''
        let specialType = ['lhd', 'rhd'].includes(frontType)
          ? item.frontType.split('-')[2]
          : frontType == 'drw' && ['pantry', 'platsa'].includes(item.frontType.split('-')[1])
          ? item.frontType.split('-')[1]
          : ''

        const isCorner = ['cnr'].includes(specialType)
        const cornerFillerPerimeter = cabinetType == 'w' ? (100 + 120) * 2 + 4 * height : (60 + 80) * 2 + 4 * height
        const perimeter = isCorner
          ? ((width + height) * 2 + cornerFillerPerimeter) / singleMuPerimeter
          : ((width + height) * 2 * qty) / singleMuPerimeter // linear metre

        // set cutting front mu factor
        let frontCutMu = 0

        // find CP cutMu
        if (coverPanelType && taskType == 'cut') {
          frontCutMu += cutEdgeMuFactor * perimeter
          // find if theres CP lighting
          if (item?.lighting) {
            frontCutMu += lightingCutMuFactor
          }
          materialMu += frontCutMu
        }

        // find Front-door cutMu
        if (frontType && taskType == 'cut') {
          // find EDGE Cut Mu * perimeter
          frontCutMu += cutEdgeMuFactor * perimeter

          // calculate HINGE Cut Mu
          if (['lhd', 'rhd'].includes(frontType) && !specialType) {
            if (height < 1600) {
              frontCutMu += hingeCutMuFactor * 1.5 // 3 hinges
            } else if (height < 2000) {
              frontCutMu += hingeCutMuFactor * 2 // 4 hinges
            } else {
              frontCutMu += hingeCutMuFactor * 2.5 // 5 hinges
            }
          }

          if (['drw'].includes(frontType) && !specialType) {
            frontCutMu += drwCutMuFactor
          }

          if (specialType) {
            switch (specialType) {
              case '2part':
                frontCutMu += hingeCutMuFactor * 1.5 // 3 hinges
                break
              case 'pax':
                if (height < 2000) {
                  frontCutMu += hingeCutMuFactor * 1.5 // 3 hinges
                } else {
                  frontCutMu += hingeCutMuFactor * 2 // 4 hinges
                }
                break
              case 'paxsplit':
                if (height < 1000) {
                  frontCutMu += hingeCutMuFactor / 2 // 1 hinge
                } else if (height < 1300) {
                  frontCutMu += hingeCutMuFactor // 2 hinges
                } else {
                  frontCutMu += hingeCutMuFactor * 1.5 // 3 hinges
                }
                break
              case 'pantry':
                frontCutMu += drwCutMuFactor * 2 // 2 sets of drawer holes
                break
              case 'platsa':
                if (frontType == 'drw') {
                  frontCutMu += platsaDrwCutMuFactor // 1 set of platsa drawer holes
                } else {
                  if (height < 1400) {
                    frontCutMu += hingeCutMuFactor * 1.5 // 3 hinges
                  } else {
                    frontCutMu += hingeCutMuFactor * 2 // 4 hinges
                  }
                }
                break
              default:
                frontCutMu += 0 // do nothing
                break
            }
          }

          // add Handles cutMu factor - pulled data from MA Handles Settings from db.
          let handleCutMu = 0
          if (item?.handle) {
            let handle = handleOptions.find((handle) => {
              return handle.id == item.handle
            })
            let masterHandle = MasterPricing.getHandle(handle.value)

            // Linear Handle cutMu Calculations (J-profile & Invisipull)
            if (['j', 'invisipull', 'invisipull-short'].includes(handle.value)) {
              // find orientation (for J-Profile)
              if (handle?.orientation !== undefined) {
                switch (handle.orientation) {
                  case 'h':
                    handleCutMu += masterHandle.cutMu * (item.w / singleMuPerimeter) * qty
                    break
                  case 'v':
                    handleCutMu += masterHandle.cutMu * (item.h / singleMuPerimeter) * qty
                    break
                }
              } else {
                // invisipull has no orientation, always width
                handleCutMu += masterHandle.cutMu * (item.w / singleMuPerimeter) * qty
              }
            } else {
              handleCutMu += masterHandle.cutMu * qty
            }
          }

          materialMu += frontCutMu + handleCutMu
        }

        if (taskType == 'finish') {
          // calculate total surface area in square meterage
          const area = !item?.doubleSided
            ? (width * height * qty) / singleMuArea
            : (width * height * 2 * qty) / doubleMuArea

          let frontFinishMu = 0
          let handleFinishMu = 0

          // edge
          const materialEdgeFactor = material.hasGrain ? veneerEdgeMuFactor : laminateEdgeMuFactor
          frontFinishMu += perimeter * materialEdgeFactor

          // surface
          if (material.hasGrain) {
            frontFinishMu += area * veneerDoubleSurfaceMuFactor
          }

          // handle
          if (item.handle) {
            let handle = handleOptions.find((handle) => {
              return handle.id == item.handle
            })
            let masterHandle = MasterPricing.getHandle(handle.value)

            // Linear Handle Mu Calculations (J-profile & Invisipull)
            if (['j', 'invisipull', 'invisipull-short'].includes(handle.value)) {
              // find orientation (for J-Profile)
              if (handle.orientation !== undefined) {
                switch (handle.orientation) {
                  case 'h':
                    handleFinishMu += masterHandle.finishMu * (item.w / singleMuPerimeter) * qty
                    break
                  case 'v':
                    handleFinishMu += masterHandle.finishMu * (item.h / singleMuPerimeter) * qty
                    break
                }
              } else {
                // invisipull has no orientation, always width
                handleFinishMu += masterHandle.finishMu * (item.w / singleMuPerimeter) * qty
              }
            } else {
              handleFinishMu += masterHandle.finishMu * qty
            }
          }

          materialMu += frontFinishMu + handleFinishMu
        }

        // add pack - WIP
        if (taskType == 'pack') {
          let packMu = 0
          // find if item is Cover Panel
          if (item?.coverPanelType) {
            if (['plinth'].includes(item?.coverPanelType)) {
              // plinth timings
              packMu += plinthPackMuFactor * qty
            } else if ((width * height) / singleMuArea >= largePackLimiter) {
              // large cover panel timings
              packMu += largePackMuFactor * qty
            } else {
              // small cover panel timings
              packMu += packMuFactor * qty
            }
          }
          // find if item is Front
          if (frontType) {
            // fronts timings
            packMu += packMuFactor * qty
          }

          materialMu += packMu
        }
      }

      o[material.id] = utils.toFixedNumber(materialMu, 2)
    })
    return o
  }
}
